import firebase from 'firebase/app';
import { useEffect, useState } from 'react';
import * as hikeService from 'services/hitchit';
import { HikeEvent } from 'services/hitchit/types/hike/HikeEvents';
import { BaseUseCase } from 'services/apps/usecases';
import useUpdateUrl from 'components/HitchById/HitchNextSteps/utils/useUpdateUrl';
import { useRouter } from 'next/router';
import { HitchHookResponse } from 'services/firebase/hooks/useHitch';
import useProfile from 'components/private/hooks/useProfile';
import { Hike } from 'services/hitchit/types/hike/Hike';
import { formatMessage } from 'services/hitchit/types/hike/HikeError';
import { Preferences } from 'services/hitchit/types/Choices';
import useConnectConfiguration from './useConnectConfiguration';

export type HitchConfiguration = {
  isFulfilled: boolean;
  currentHike?: Hike;
  onHasPendingChanges: (hasPendingChanges?: Preferences) => void;
  createHike: () => Promise<void>;
};

export type HitchGoLive = {
  goLive: () => Promise<void>;
  selectedBillingPackage: string;
  setSelectedBillingPackage: (key: string) => void;
  hikeEvents?: HikeEvent[];
  goLiveBusy: boolean;
  isLive: boolean;
};

export type UseCaseConfiguration = {
  selectedUseCase?: BaseUseCase;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  errorMessage?: string;
  setErrorMessage: () => void;
  usecases: BaseUseCase[];
  setSelectedUseCase: (useCase?: BaseUseCase) => void;
};

export type ConnectConfiguration = {
  hitch: HitchHookResponse;
  firebaseUser?: firebase.User;
  authLoading?: boolean;
};

export type HitchConfigurationHook = {
  hitchId: string;
  connectConfiguration: ConnectConfiguration;
  useCaseConfiguration: UseCaseConfiguration;
  hitchConfiguration: HitchConfiguration;
  hitchGoLive: HitchGoLive;
  // in case a hike is active/paused of the same hitchIds, route to dashboard
  // limitation: only one use case of the same hitchIds can be active for now
  // why? because this would give very strange edge cases (double running creations of deals etc...)
  activeHike?: Hike;
  loading: boolean;
};

export type BackendError = 'authenticate' | undefined;

export default function useHitchConfiguration(
  hitchId: string,
  usecases: BaseUseCase[]
): HitchConfigurationHook {
  const { user, hikes, loading } = useProfile();
  const connectConfiguration = useConnectConfiguration(hitchId);
  const { hitch } = connectConfiguration;
  const [selectedBillingPackage, setSelectedBillingPackage] = useState<string>('pro');
  const [currentPage, setCurrentPage] = useState(0);

  const { query } = useRouter();
  const isConnected = !!hitch?.data?.isConnected;
  const activeHike = hikes
    ? hikes.find((hike) => hike.hitchRef === hitch.data?.hitchRef && hike.status === 'active')
    : undefined;
  const [selectedUseCase, setSelectedUseCase] = useState<BaseUseCase | undefined>();

  const currentHike =
    hikes &&
    hikes.find(
      (hike) => hike.hitchRef === hitch.data?.hitchRef && hike.useCaseId === selectedUseCase?.id
    );

  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const { updateUrl, removeFromUrl } = useUpdateUrl();

  const [pendingPreferenceChanges, setPendingPreferenceChanges] = useState<Preferences>();
  const [creatingHike, setCreatingHike] = useState<boolean>(false);

  /**
   * Create the draft hike when the connections are established
   */

  async function createHike() {
    if (
      !errorMessage &&
      isConnected &&
      !loading &&
      !currentHike &&
      selectedUseCase &&
      user &&
      !creatingHike &&
      hitch?.data?.hitchRef
    ) {
      setCreatingHike(true);

      const result = await hikeService.createHike(
        { useCaseId: selectedUseCase?.id, hitchRef: hitch.data.hitchRef },
        user
      );

      if (result.error) {
        setErrorMessage(formatMessage(result.error.code));
      }
      setCreatingHike(false);
    }
  }

  useEffect(() => {
    if (!selectedUseCase && usecases && usecases.length > 0 && !query.usecase) {
      setSelectedUseCase(usecases[0]);
    }
  }, []);

  useEffect(() => {
    if (!query.usecase) {
      if (selectedUseCase) {
        setSelectedUseCase(undefined);
      }
      return;
    }

    // use case from url is different then the selected one
    if (selectedUseCase?.id !== query.usecase) {
      // use case from url is a matching use case
      const matchingUseCase = usecases.find((uc) => uc.id === query.usecase);
      if (matchingUseCase) {
        setSelectedUseCase(matchingUseCase);
      } else if (selectedUseCase) {
        //use case from url does not match
        // remove selectedUseCase
        setSelectedUseCase(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.usecase]);

  const [goLiveBusy, setGoLiveBusy] = useState(false);
  const [isLive, setIsLive] = useState(false);

  async function goLive() {
    if (!user) {
      throw new Error('Go Live not working because no Firebase user');
    }
    if (!selectedUseCase) {
      throw new Error('Go Live not working because no useCase chosen');
    }
    if (!currentHike) {
      throw new Error('Go Live not working because no current hike');
    }
    setGoLiveBusy(true);
    await hikeService.activateHike(currentHike.id, user, selectedBillingPackage);
    setIsLive(true);
    setGoLiveBusy(false);
  }

  const onSelectedUsecase = (usecase?: BaseUseCase) => {
    setErrorMessage(undefined);

    if (!usecases.find((uc) => uc.id === usecase?.id)) {
      removeFromUrl({ usecase: true });
      return;
    }

    if (usecase) {
      updateUrl({ usecase: usecase.id });
    } else {
      removeFromUrl({ usecase: true });
    }
  };

  return {
    hitchId,
    useCaseConfiguration: {
      setSelectedUseCase: onSelectedUsecase,
      currentPage,
      setCurrentPage,
      selectedUseCase,
      errorMessage,
      setErrorMessage: () => setErrorMessage(undefined),
      usecases,
    },
    connectConfiguration,
    hitchConfiguration: {
      currentHike,
      onHasPendingChanges: setPendingPreferenceChanges,
      isFulfilled: !pendingPreferenceChanges,
      createHike,
    },
    hitchGoLive: { goLive, goLiveBusy, isLive, selectedBillingPackage, setSelectedBillingPackage },
    activeHike,
    loading: loading || creatingHike,
  };
}
